import { getRouteApi } from '@tanstack/react-router'
import { isToday } from 'date-fns'
import { capitalize } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import PlusIcon from '@/assets/icons/add.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import UndoIcon from '@/assets/icons/undo.svg?react'
import Alert from '@/components/Alert/Alert'
import Button from '@/components/Button/Button'
import DataBlock from '@/components/DataBlock/DataBlock'
import Label from '@/components/Label'
import LinkText from '@/components/LinkText/LinkText'
import SemesterSelect from '@/components/common/SemesterSelect/SemesterSelect'
import ToggleFavourite from '@/components/common/ToggleFavourite/ToggleFavourite'
import { USER_PROFILE } from '@/constants/user-profile'
import { UserAvailabilityModal } from '@/modules/availabilityManagement'
import useAuthStore from '@/store/useAuthStore'
import { formatDate } from '@/utils/format-date'

import styles from './UserDetailsView.module.scss'
import AvailabilityDataBlock from '../components/AvailabilityDataBlock'
import useScheduleArchiveUsers from '../hooks/useScheduleArchiveUsers'
import useUser from '../queries/useUser'
import { useUserAvailability } from '../queries/useUserAvailability'
import { useUserAvailabilitySemesters } from '../queries/useUserAvailabilitySemesters'

const routeApi = getRouteApi('/_auth/users/$userId/details')

const UserDetailsView = () => {
  const navigate = routeApi.useNavigate()
  const { userId } = routeApi.useParams()
  const { semesterId, setAvailability } = routeApi.useSearch()
  const { data: user, refetch } = useUser(userId)
  const { user: userStore } = useAuthStore()
  const { t } = useTranslation('users')

  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    if (setAvailability) setIsModalOpen(true)
  }, [setAvailability])

  const { data: semesters, isLoading: isSemesterLoading } =
    useUserAvailabilitySemesters({ userId })

  const { data: userAvailability } = useUserAvailability({
    userId,
    semester: semesterId
  })

  const { mutate: bulkScheduledArchiveUser } = useScheduleArchiveUsers({
    userName: user?.name,
    onSuccess: () => {
      refetch()
    }
  })

  const basicInformationInfo = [
    { label: t('label.gender'), value: capitalize(user?.gender) },
    { label: t('label.birth'), value: formatDate(user?.dateOfBirth) },
    {
      label: t('label.nationality'),
      value: user?.nationalities.map(item => item.label)
    },
    { label: t('label.first-language'), value: user?.firstLanguage?.label },
    { label: t('label.community'), value: user?.community?.label },
    {
      label: t('label.ahv-number'),
      value: user?.ahvNumber
    }
  ]

  const academicInfo = [
    {
      label: t('label.class-tutor'),
      value: user?.tutor?.classes.length ? (
        <>
          {user?.tutor?.classes.map(classItem => (
            <LinkText
              size="medium"
              key={classItem.id}
              to="/students-and-classes/classes/$classId/details"
              className={styles.classItem}
              params={{
                classId: classItem.id
              }}
            >
              {classItem.name}
            </LinkText>
          ))}
        </>
      ) : (
        '—'
      ),
      hidden: !user?.profiles.includes(USER_PROFILE.TUTOR)
    },
    {
      label: t('label.preferred-room'),
      value: user?.teacher?.preferredRooms?.map(room => room.name) || [],
      hidden:
        !user?.profiles.includes(USER_PROFILE.TUTOR) &&
        !user?.profiles.includes(USER_PROFILE.TEACHER)
    }
  ].filter(item => !item.hidden)

  const contactDetailsInfo = [
    { label: t('label.phone-number'), value: user?.phoneNumber },
    { label: t('label.email'), value: user?.email },
    { label: t('label.emergency-contact'), value: user?.emergencyPhoneNumber }
  ]

  const handleChangeSemester = (semester: string) => {
    navigate({
      search: {
        semesterId: semester
      }
    })
  }

  const isArchived = user?.status === 'archived'

  const canEditUser = userStore?.isSuperAdmin && !isArchived

  const canEditAvailability =
    (userStore?.id === userId || userStore?.isSuperAdmin) && !isArchived

  const semester = semesters?.getSemester(semesterId)

  const scheduledDateToArchive = user?.scheduledArchiveDate

  const handleUndoScheduledArchive = () => {
    bulkScheduledArchiveUser({
      ids: [userId]
    })
  }

  return (
    <>
      {isArchived ? (
        <Alert
          message={t('help.user-archived-at', {
            DATE: formatDate(user?.archivedAt)
          })}
          className={styles.archivedAlert}
        />
      ) : userStore?.isSuperAdmin && scheduledDateToArchive ? (
        <div className={styles.scheduledArchiveWrapper}>
          <Alert
            message={t('help.user-scheduled-to-be-archived', {
              date: formatDate(user?.scheduledArchiveDate)
            })}
            className={styles.archivedAlert}
          />

          {!isToday(scheduledDateToArchive) ? (
            <Button
              variant="tertiary"
              icon={<UndoIcon />}
              onClick={handleUndoScheduledArchive}
            >
              {t('button.undo')}
            </Button>
          ) : null}
        </div>
      ) : null}
      <div className={styles.container}>
        <div className={styles.header}>
          <p className={styles.details}>{t('header.details')}</p>
          <div className={styles.actions}>
            {canEditUser ? (
              <Button
                asLink
                to="/users/edit/$userId"
                params={{ userId }}
                variant="secondary"
                icon={<EditIcon />}
              >
                {t('button.edit')}
              </Button>
            ) : null}
            <ToggleFavourite />
          </div>
        </div>
        <div className={styles.wrapper}>
          <DataBlock
            header={t('header.basic-information')}
            infoArray={basicInformationInfo}
          />

          <DataBlock header={t('header.academics')} infoArray={academicInfo} />

          <DataBlock
            header={t('header.contact-details')}
            infoArray={contactDetailsInfo}
          />
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.header}>
          <p className={styles.details}>{t('header.availability')}</p>
          <div className={styles.availabilityFilters}>
            <Label id="semester-select" label={t('label.semester')} hidden>
              <SemesterSelect
                id="semester-select"
                hideSearch
                className={styles.semesterSelect}
                options={semesters?.options || []}
                value={semesterId}
                onChange={handleChangeSemester}
                loading={isSemesterLoading}
                semesterList={semesters?.list || []}
              />
            </Label>
            {canEditAvailability ? (
              <Button
                icon={<PlusIcon />}
                variant="secondary"
                onClick={() => setIsModalOpen(true)}
                disabled={semester?.status === 'archived'}
              >
                {t('button.set-availability')}
              </Button>
            ) : null}
          </div>
        </div>

        <AvailabilityDataBlock availability={userAvailability} />
      </div>
      {semester ? (
        <UserAvailabilityModal
          onOpenChange={value => {
            setIsModalOpen(value)

            if (!value && setAvailability) {
              navigate({
                search: previousValue => ({
                  ...previousValue,
                  setAvailability: false
                })
              })
            }
          }}
          open={isModalOpen}
          semester={semester}
          userId={userId}
          userName={user ? user.name : ''}
        />
      ) : null}
    </>
  )
}

export default UserDetailsView
